import React, { useState, useRef, useEffect, useCallback } from "react";
import classnames from "classnames";
import { Modal, Button, ListGroup } from "react-bootstrap";

import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import OutletRating from "../shared/OutletRating";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
const SEARCH_DEBOUNCE_MS = 300;

function OutletPicker({ show, onClose, onSelect, currentOutletId, currentOutletName }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const debounceTimer = useRef(null);
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setLoading(true);
    setSearchError(null);
    try {
      const res = await fetch(
        `${SUPABASE_URL()}/rest/v1/admin_outlets?name=ilike.*${encodeURIComponent(searchQuery)}*&select=id,name,profile_image_url,prestige,domain_authority&order=name.asc&limit=10`,
        {
          headers: { ...authenticatedHeadersHash(), "Content-Type": "application/json" },
          signal,
        },
      );
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      setResults(await res.json());
    } catch (err) {
      if (err.name !== "AbortError") {
        setSearchError("Search failed — please try again");
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => performSearch(query), SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query, performSearch]);

  const handleSelect = async (outletId) => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSelect(outletId);
      onClose();
    } catch (err) {
      setSaveError(err.message || "Failed to update outlet");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!saving) onClose();
      }}
      onEntered={() => inputRef.current?.focus()}
    >
      <Modal.Header closeButton className="px-4 pt-4 pb-0 border-0 align-items-start">
        <div>
          <Modal.Title className="paragraph-lg fw-bold">Change Outlet</Modal.Title>
          {currentOutletId && (
            <p className="text-secondary paragraph-sm mb-0 mt-1">
              Currently set to <strong>{currentOutletName}</strong>
            </p>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <input
          ref={inputRef}
          type="text"
          className={classnames("form-control", { "mb-3": query })}
          placeholder="Search outlets..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={saving}
        />

        {saveError && <div className="alert alert-danger paragraph-sm mb-2">{saveError}</div>}
        {searchError && <div className="alert alert-warning paragraph-sm mb-2">{searchError}</div>}

        {loading && (
          <div className="text-center py-3">
            <i className="fa fa-spinner fa-spin text-muted" />
          </div>
        )}

        {!loading && query && results.length === 0 && (
          <p className="text-secondary text-center py-3 paragraph-sm mb-0">No outlets found</p>
        )}

        {results.length > 0 && (
          <ListGroup variant="flush" className="border rounded-2">
            {results.map((outlet) => {
              const isCurrent = outlet.id === currentOutletId;
              return (
                <ListGroup.Item
                  key={outlet.id}
                  action
                  active={isCurrent}
                  disabled={saving || isCurrent}
                  onClick={() => handleSelect(outlet.id)}
                  className="d-flex align-items-center"
                >
                  {outlet.profile_image_url ? (
                    <img
                      src={outlet.profile_image_url}
                      alt=""
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                      className="me-3"
                    />
                  ) : (
                    <div
                      className="me-3 bg-light d-flex align-items-center justify-content-center rounded-1"
                      style={{ width: 28, height: 28 }}
                    >
                      <i className="fa fa-building text-muted paragraph-xs" />
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <span className="paragraph-md fw-bold">{outlet.name}</span>
                  </div>
                  <OutletRating prestige={outlet.prestige} domainAuthority={outlet.domain_authority} />
                  {isCurrent && (
                    <span className="badge bg-light text-dark ms-2">
                      <i className="fa fa-check me-1" />
                      Current
                    </span>
                  )}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}

        {saving && (
          <div className="text-center py-2">
            <i className="fa fa-spinner fa-spin me-1" />
            <span className="paragraph-sm">Saving...</span>
          </div>
        )}
      </Modal.Body>
      {currentOutletId && (
        <Modal.Footer className="px-4 pb-4 pt-0 border-0">
          <Button className="w-100" variant="outline-danger" onClick={() => handleSelect(null)} disabled={saving}>
            <i className="fas fa-times-circle me-2" />
            Clear current outlet
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default OutletPicker;
